// src/components/panel/HistoricalTrends.js
import React, { useState, useEffect } from 'react';
import { DotChartOutlined, RiseOutlined, FallOutlined } from '@ant-design/icons';
import { Spin } from 'antd';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { analyticsService } from '../../services/analyticsService';

function HistoricalTrends() {
    const [data, setData] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [period, setPeriod] = useState(7);

    useEffect(() => {
        loadData();
    }, [period]);

    const loadData = async () => {
        try {
            setLoading(true);
            const response = await analyticsService.getSalesTrend(period);
            setData(response.data || []);
            setSummary(response.summary || null);
            setError(null);
        } catch (err) {
            console.error('Error loading sales trend:', err);
            setError('Error al cargar tendencias');
        } finally {
            setLoading(false);
        }
    };

    const getPeriodLabel = () => {
        switch (period) {
            case 7:
                return 'ÚLTIMOS 7 DÍAS';
            case 30:
                return 'ÚLTIMOS 30 DÍAS';
            case 90:
                return 'ÚLTIMOS 90 DÍAS';
            default:
                return 'ÚLTIMOS 7 DÍAS';
        }
    };

    const formatCurrency = (value) => {
        return `$${value.toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    };

    const formatDate = (dateStr) => {
        const date = new Date(dateStr + 'T00:00:00');
        return date.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' });
    };

    if (loading) {
        return (
            <div className='card'>
                <div className='card-inner'>
                    <DotChartOutlined className='card_icon' style={{ fontSize: '38px' }} />
                    <h2>TENDENCIAS HISTÓRICAS</h2>
                </div>
                
                <div className='card-content-center'>
                    <Spin tip="Cargando tendencias..." />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className='card'>
                <div className='card-inner'>
                    <DotChartOutlined className='card_icon' style={{ fontSize: '38px' }} />
                    <h2>TENDENCIAS HISTÓRICAS</h2>
                </div>
                
                <div className='card-content-center' style={{ color: '#ff4d4f' }}>
                    {error}
                </div>
            </div>
        );
    }

    const totalSales = summary?.TotalSales || 0;
    const avgDailySales = summary?.AvgDailySales || 0;
    const peakSales = summary?.PeakSales || 0;
    const peakDay = summary?.PeakDay || '';

    return (
        <div className='card'>
            <div className='card-inner'>
                <DotChartOutlined className='card_icon' style={{ fontSize: '38px' }} />
                <h2>TENDENCIAS {getPeriodLabel()}</h2>
            </div>
            
            {/* Period selector buttons */}
            <div className='category-period-selector' style={{ marginTop: '8px', marginBottom: '12px' }}>
                <button 
                    className={`category-period-btn ${period === 7 ? 'active' : ''}`}
                    onClick={() => setPeriod(7)}
                >
                    7 días
                </button>
                <button 
                    className={`category-period-btn ${period === 30 ? 'active' : ''}`}
                    onClick={() => setPeriod(30)}
                >
                    30 días
                </button>
                <button 
                    className={`category-period-btn ${period === 90 ? 'active' : ''}`}
                    onClick={() => setPeriod(90)}
                >
                    90 días
                </button>
            </div>

            {/* Summary Stats */}
            <div className='trends-summary'>
                <div className='trends-summary-item'>
                    <div className='trends-label'>Ventas Totales</div>
                    <div className='trends-value' style={{ color: '#1890ff' }}>
                        {formatCurrency(totalSales)}
                    </div>
                </div>
                <div className='trends-summary-item'>
                    <div className='trends-label'>Promedio Diario</div>
                    <div className='trends-value' style={{ color: '#52c41a' }}>
                        {formatCurrency(avgDailySales)}
                    </div>
                </div>
                <div className='trends-summary-item'>
                    <div className='trends-label'>Día Pico</div>
                    <div className='trends-value' style={{ color: '#faad14' }}>
                        {peakDay ? formatDate(peakDay) : '-'}
                    </div>
                    <div className='trends-sublabel'>{formatCurrency(peakSales)}</div>
                </div>
            </div>

            {/* Line Chart */}
            <div style={{ marginTop: '16px', height: '250px' }}>
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis 
                            dataKey="Date" 
                            tickFormatter={formatDate}
                            tick={{ fontSize: 11 }}
                            stroke="#8c8c8c"
                        />
                        <YAxis 
                            tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                            tick={{ fontSize: 11 }}
                            stroke="#8c8c8c"
                        />
                        <Tooltip 
                            formatter={(value, name) => {
                                if (name === 'TotalSales') return [formatCurrency(value), 'Ventas'];
                                if (name === 'AvgInvoiceValue') return [formatCurrency(value), 'Promedio'];
                                return [value, name];
                            }}
                            labelFormatter={(label) => formatDate(label)}
                            contentStyle={{ fontSize: '12px' }}
                        />
                        <Legend 
                            wrapperStyle={{ fontSize: '12px' }}
                            formatter={(value) => {
                                if (value === 'TotalSales') return 'Ventas Totales';
                                if (value === 'AvgInvoiceValue') return 'Ticket Promedio';
                                return value;
                            }}
                        />
                        <Line 
                            type="monotone" 
                            dataKey="TotalSales" 
                            stroke="#1890ff" 
                            strokeWidth={2}
                            dot={{ fill: '#1890ff', r: 3 }}
                            activeDot={{ r: 5 }}
                        />
                        <Line 
                            type="monotone" 
                            dataKey="AvgInvoiceValue" 
                            stroke="#52c41a" 
                            strokeWidth={2}
                            dot={{ fill: '#52c41a', r: 3 }}
                            activeDot={{ r: 5 }}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

export default HistoricalTrends;
